import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import type { IntlShape } from 'react-intl';
import {
    doseUnitLabel,
    formatDisplayIntensity,
    formatPetDisplayValue,
    ImageUnitSuffix,
    withUnit,
} from '~/domain/dicom/intensityUnits';
import { Modality } from '~/domain/dicom/modality';
import { formatAngle } from '~/utils/obliquity';
import { DirectionLabel, oppositeDirection } from '~/utils/orientation';
import {
    calculateNormalizedOrientationValue,
    DENSITY_OVERRIDE_DECIMALS,
    formatMmAsCm,
    formatMmAsCmAtScale,
} from '~/utils/units';
import type { FrameAxis } from '~/hooks/viewer/useFrameRotationReadout';
import {
    ClientPoint,
    CursorValue,
    DatasetMetadata,
    DensityCursorValue,
    DoseCursorValue,
    DrawnImage,
    EdgeDirectionLabels,
    ImageCursorReadout,
    PlanePosition,
    RenderableVolume,
    RenderAdapter,
    SliceInfo,
} from '../adapters/types';
import { ImageLayer, Plane } from '../layout/types';
import {
    PairEnd,
    pairEndLabels,
    SeriesNumberFormatter,
} from '../pairEndLabels';
import type { PointMm } from '~/domain/geometry/patientGeometry';
import type { RoiId } from '~/domain/changes/ids';

/**
 * Pure HUD formatting for one tile. No React, no canvas — every safety-
 * relevant readout rule (units always labelled, never Hounsfield on a
 * non-CT modality, cm precision per docs/ui_specs.md) lives here and is
 * unit-tested independently of rendering.
 */

/** The image a tile shows, as far as its labels go. */
export type ShownImage = Pick<
    DatasetMetadata,
    'seriesInstanceUid' | 'label' | 'modality' | 'seriesNumber'
>;

/**
 * The image a pane draws, for its labels: the row's dataset, or the fused
 * secondary when the pane draws that side alone. Undefined when the pane
 * asks for a secondary the row does not have, so nothing is ever drawn
 * under another image's name.
 */
export function shownImageOfPane(
    layer: ImageLayer,
    dataset: ShownImage,
    secondary: ShownImage | undefined
): ShownImage | undefined {
    return layer === ImageLayer.Secondary ? secondary : dataset;
}

/**
 * The fused image a pane names beside the row's image: the fused secondary
 * on a pane that blends it, none on any other pane.
 */
export function fusedImageNamedByPane(
    layer: ImageLayer,
    fused: ShownImage | undefined
): ShownImage | undefined {
    return layer === ImageLayer.Blended ? fused : undefined;
}

/** A drawn image as the tile reads it: a pair end, its name and its origin. */
export type DrawnSource = PairEnd &
    Pick<DatasetMetadata, 'seriesInstanceUid'> &
    Partial<
        Pick<
            DatasetMetadata,
            | 'label'
            | 'generatedBy'
            | 'interpolatedSlices'
            | 'acquisitionTiltDeg'
        >
    >;

/**
 * The images a pane draws: the row's own and the fused secondary when it
 * blends them; the row's own alone when it draws that side; the fused
 * secondary alone when it draws the other. The row's own leads.
 */
export function sourcesDrawnByPane(
    layer: ImageLayer,
    rowImage: DrawnSource,
    fused: DrawnSource | undefined
): ReadonlyArray<DrawnSource> {
    switch (layer) {
        case ImageLayer.Primary:
            return [rowImage];
        case ImageLayer.Secondary:
            return fused === undefined ? [] : [fused];
        case ImageLayer.Blended:
            return fused === undefined ? [rowImage] : [rowImage, fused];
    }
}

/**
 * The images a pane draws, as {@link sourcesDrawnByPane} lists them. Each
 * image of a blended pair is named by {@link pairEndLabels}, with its series
 * number written by `nameSeries`; an image drawn alone carries no pair name.
 */
export function imagesDrawnByPane(
    layer: ImageLayer,
    rowImage: DrawnSource,
    fused: DrawnSource | undefined,
    nameSeries: SeriesNumberFormatter
): ReadonlyArray<DrawnImage> {
    const pairLabels =
        layer === ImageLayer.Blended && fused !== undefined
            ? pairEndLabels(rowImage, fused, nameSeries)
            : undefined;
    return sourcesDrawnByPane(layer, rowImage, fused).map((image, index) => ({
        seriesInstanceUid: image.seriesInstanceUid,
        pairLabel: pairLabels?.[index],
        label: image.label,
        generatedBy: image.generatedBy,
        interpolatedSlices: image.interpolatedSlices,
        acquisitionTiltDeg: image.acquisitionTiltDeg,
        // A blended pane's readout follows the row's image, listed first.
        ...(pairLabels !== undefined && index > 0
            ? { readoutFollowsOtherImage: true }
            : {}),
    }));
}

/** Selects the ROI a click landed on; `focus` on a double-click. */
export type RoiSelectionHandler = (roiId: RoiId, focus: boolean) => void;

/**
 * Whether a volume belongs to the editable set: the series that owns the
 * active structure set.
 */
function isEditableSeries(
    volume: RenderableVolume,
    editableSeriesUid: SeriesInstanceUid | undefined
): boolean {
    return (
        editableSeriesUid !== undefined &&
        volume.metadata.seriesInstanceUid === editableSeriesUid
    );
}

/**
 * The handler a click on a pane of `volume` selects an ROI through, or
 * undefined while no click can select: outside pan mode (a draw click is a
 * stroke), or on a pane outside the editable set (a picked structure must
 * belong to the active structure set the selection resolves against).
 */
export function roiSelectionFor(
    volume: RenderableVolume,
    editableSeriesUid: SeriesInstanceUid | undefined,
    canSelectRoi: boolean,
    onSelectRoi: RoiSelectionHandler | undefined
): RoiSelectionHandler | undefined {
    return canSelectRoi && isEditableSeries(volume, editableSeriesUid)
        ? onSelectRoi
        : undefined;
}

/**
 * What a tile's hover label offers on the structure under the pointer: the
 * contour deletions of the structure being edited.
 */
export interface TileStructureActions {
    /** Whether the label offers the actions over this ROI. */
    offersFor(roiId: RoiId): boolean;
    /**
     * Opens the actions over the tile of `renderer`, on the contour at the
     * client point `pick`, shown at the client point `anchor`.
     */
    open(renderer: RenderAdapter, pick: ClientPoint, anchor: ClientPoint): void;
}

/**
 * The structure actions a pane of `volume` offers, or undefined on a pane
 * outside the editable set: its structures are not the ones being edited.
 */
export function structureActionsFor(
    volume: RenderableVolume,
    editableSeriesUid: SeriesInstanceUid | undefined,
    actions: TileStructureActions | undefined
): TileStructureActions | undefined {
    return isEditableSeries(volume, editableSeriesUid) ? actions : undefined;
}

/**
 * Whether this pane names the image it draws in its title bar.
 *
 * A row names an image once, at its first image pane, so a tri-planar row
 * of one image carries one name rather than three. A pane drawing the
 * fused secondary alone always names it: its neighbour draws something
 * else, and two panes of one row must never be told apart by position
 * alone.
 *
 * @param leadsRow Whether the pane is the first image pane of its row.
 */
export function paneNamesItsImage(
    layer: ImageLayer,
    leadsRow: boolean
): boolean {
    return leadsRow || layer === ImageLayer.Secondary;
}

export interface EdgeLabels {
    readonly top: string;
    readonly bottom: string;
    readonly left: string;
    readonly right: string;
}

function baseEdgeLabels(plane: Plane): EdgeLabels {
    switch (plane) {
        case Plane.Transversal:
            return { top: 'A', bottom: 'P', left: 'R', right: 'L' };
        case Plane.Sagittal:
            return { top: 'H', bottom: 'F', left: 'A', right: 'P' };
        case Plane.Coronal:
            return { top: 'H', bottom: 'F', left: 'R', right: 'L' };
    }
}

/**
 * Radiological edge letters (R/L = patient right/left, A/P = anterior/
 * posterior, H/F = head/feet). When the mounted view's real direction letters
 * are supplied (from {@link RenderAdapter.getEdgeLabels}, [left, top, right,
 * bottom]) the letters follow the actual orientation, so a flipped or oblique
 * view relabels R/L and can never mislabel patient laterality. Before the view
 * mounts the orientation is unknown, so it falls back to the canonical
 * head-first-supine letters for the plane.
 */
export function edgeLabelsForPlane(
    plane: Plane,
    directionLabels?: EdgeDirectionLabels
): EdgeLabels {
    if (!directionLabels) {
        return baseEdgeLabels(plane);
    }
    const [left, top, right, bottom] = directionLabels;
    return { top, bottom, left, right };
}

/** The message id of each plane's name. */
export const PLANE_MESSAGE_IDS: Readonly<Record<Plane, string>> = {
    [Plane.Transversal]: 'planeTransversal',
    [Plane.Sagittal]: 'planeSagittal',
    [Plane.Coronal]: 'planeCoronal',
};

/** A plane's name in the user's language, as a sentence says it, e.g. "Transversal". */
export function planeName(intl: IntlShape, plane: Plane): string {
    return intl.formatMessage({ id: PLANE_MESSAGE_IDS[plane] });
}

/**
 * A plane's name as a tile names its plane, in upper case in the user's
 * language, e.g. "TRANSVERSAL".
 */
export function planeLabel(intl: IntlShape, plane: Plane): string {
    return planeName(intl, plane).toLocaleUpperCase(intl.locale);
}

/** An image as a list names it: its series, modality and name. */
export interface ListedImage {
    readonly seriesInstanceUid: string;
    readonly modality: string;
    readonly label: string;
}

/**
 * Whether another series of `images` has the same modality and name as
 * `image`, so that only its series number tells the two rows apart. A
 * series listed more than once, as one per registration, does not count
 * against itself.
 */
export function sharesNameWithAnotherSeries(
    image: ListedImage,
    images: ReadonlyArray<ListedImage>
): boolean {
    return images.some(
        (other) =>
            other.seriesInstanceUid !== image.seriesInstanceUid &&
            other.modality === image.modality &&
            other.label === image.label
    );
}

/**
 * A series number as the console reads it, e.g. "Series 3". The number is a
 * DICOM code, so it is never grouped by the locale.
 */
export function seriesNumberText(
    intl: IntlShape,
    seriesNumber: number
): string {
    return intl.formatMessage(
        { id: 'series.number' },
        { number: String(seriesNumber) }
    );
}

/**
 * The series number shown after an image's name, e.g. "Series 3", when the
 * name alone does not tell the image apart: while patient information is
 * hidden, so images of one modality still differ while their names are
 * masked, and when another listed series shares the image's modality and
 * name. Undefined otherwise, and when the image has no series number.
 */
export function distinguishingSeriesNumber(
    intl: IntlShape,
    seriesNumber: number | undefined,
    namesShown: boolean,
    sharesName = false
): string | undefined {
    return seriesNumber != null && (sharesName || !namesShown)
        ? seriesNumberText(intl, seriesNumber)
        : undefined;
}

/**
 * The spoken name of a pane that draws `image` on `plane`: the modality,
 * the series number when there is one, and the plane, e.g. "CT Series 3
 * TRANSVERSAL". The series description is PHI and never part of it, so a
 * screen reader says the same whatever the privacy toggle hides.
 */
export function paneAccessibleName(
    intl: IntlShape,
    image: Pick<ShownImage, 'modality' | 'seriesNumber'>,
    plane: Plane
): string {
    return [
        image.modality,
        image.seriesNumber != null
            ? seriesNumberText(intl, image.seriesNumber)
            : undefined,
        planeLabel(intl, plane),
    ]
        .filter((part) => part !== undefined)
        .join(' ');
}

/** What a readout shows in place of a number the image has none for. */
export const NO_VALUE = '–';

/**
 * Where a view sits in the image it shows, and how finely that image is
 * sampled. `slice` names the slice when the plane lies in the image's
 * slice planes; `oblique` has no slice to name.
 */
export type SliceIndicator =
    | {
          readonly kind: 'slice';
          /**
           * The slice the view shows, counted from 1, e.g. "143", or
           * {@link NO_VALUE} when the plane lies outside the image. It
           * moves as the user scrolls.
           */
          readonly index: string;
          /** How many slices the image holds along the plane's normal, e.g. "287". */
          readonly count: string;
          /** Whether the slice shown was interpolated rather than acquired. */
          readonly interpolated: boolean;
          /** The patient directions slice 1 and the last slice lie toward. */
          readonly numbering: SliceNumbering;
          readonly sampling: SamplingText;
      }
    | {
          readonly kind: 'oblique';
          /**
           * How far the plane lies off the image's slices in degrees to
           * one decimal, with no unit, e.g. "29.8".
           */
          readonly angle: string;
          readonly cause: ObliqueCauseText;
          readonly sampling: SamplingText;
      };

/**
 * How the image is sampled, at voxel scale (3 dp) with the unit: its
 * pixel size in each slice, "0.074 cm", or "0.074 × 0.075 cm" when its
 * rows and columns differ; the distance between its slices; and the
 * slice thickness its DICOM states, undefined when it states none.
 */
export interface SamplingText {
    readonly pixelSize: string;
    readonly sliceSpacing: string;
    readonly sliceThickness: string | undefined;
}

/** The patient directions an image's slice numbers run from and toward. */
export interface SliceNumbering {
    readonly from: DirectionLabel;
    readonly toward: DirectionLabel;
}

/**
 * Which way the slice number runs through the patient, in the user's
 * language, e.g. "Numbered from feet to head."
 */
export function sliceNumberingLine(
    intl: IntlShape,
    numbering: SliceNumbering
): string {
    const name = (label: DirectionLabel) =>
        intl.formatMessage({ id: `slice.numbering.direction.${label}` });
    return intl.formatMessage(
        { id: 'slice.numbering' },
        { from: name(numbering.from), toward: name(numbering.toward) }
    );
}

/** A voxel-scale distance in mm, as the readout writes it in cm. */
function voxelCm(mm: number): string {
    return `${formatMmAsCmAtScale(mm, 'voxel')} cm`;
}

/** One labelled value of how an image is sampled. */
export interface SamplingRow {
    readonly label: string;
    readonly value: string;
}

/**
 * How the image a tile shows is sampled, as labelled values: the distance
 * between its acquired slices and the pixel size in each of them. They are
 * the image's own, the same on every plane a tile cuts, so a plane across
 * the acquired slices reads them beside a slice count along another axis.
 * The slice thickness its DICOM states joins them only where it differs
 * from the spacing, as in an overlapping reconstruction, where reading the
 * spacing as the thickness would mislead.
 */
export function samplingRows(
    intl: IntlShape,
    sampling: SamplingText
): ReadonlyArray<SamplingRow> {
    const row = (id: string, value: string): SamplingRow => ({
        label: intl.formatMessage({ id }),
        value,
    });
    const rows = [
        row('slice.sampling.between', sampling.sliceSpacing),
        row('slice.sampling.pixel', sampling.pixelSize),
    ];
    if (
        sampling.sliceThickness !== undefined &&
        sampling.sliceThickness !== sampling.sliceSpacing
    ) {
        rows.push(row('slice.sampling.thickness', sampling.sliceThickness));
    }
    return rows;
}

/**
 * Why a plane lies off the image's slices, with the image's own tilt in
 * degrees to one decimal, no unit, when the image was acquired tilted.
 */
export interface ObliqueCauseText {
    readonly acquisitionTilt: string | undefined;
    readonly registrationTilts: boolean;
    readonly viewTurned: boolean;
}

/**
 * Why a tile's plane lies `angle` degrees off the image's slices, and
 * what that withholds, in the user's language, one sentence per line:
 * how far it lies off, each reason that holds, what is not available, and
 * the remedy. Reformatting is the remedy for an image acquired tilted.
 * Resetting the row is offered only when the turn is the one reason,
 * because a reset returns to the patient axes, which are not the slices
 * of a tilted or tilt-registered image.
 */
export function obliqueReasonLines(
    intl: IntlShape,
    angle: string,
    cause: ObliqueCauseText
): ReadonlyArray<string> {
    const say = (id: string, values?: Record<string, string>) =>
        intl.formatMessage({ id }, values);
    const lines = [say('slice.oblique.lead', { angle })];
    if (cause.acquisitionTilt !== undefined) {
        lines.push(
            say('slice.oblique.cause.acquisition', {
                tilt: cause.acquisitionTilt,
            })
        );
    }
    if (cause.registrationTilts) {
        lines.push(say('slice.oblique.cause.registration'));
    }
    if (cause.viewTurned) {
        lines.push(say('slice.oblique.cause.view'));
    }
    lines.push(say('slice.oblique.withheld'));
    if (cause.acquisitionTilt !== undefined) {
        lines.push(say('slice.oblique.remedy.reformat'));
    } else if (cause.viewTurned && !cause.registrationTilts) {
        lines.push(say('slice.oblique.remedy.reset'));
    }
    return lines;
}

export function formatSliceIndicator(slice: SliceInfo): SliceIndicator {
    const pixelRow = formatMmAsCmAtScale(slice.pixelSpacingMm.row, 'voxel');
    const pixelColumn = formatMmAsCmAtScale(
        slice.pixelSpacingMm.column,
        'voxel'
    );
    const sampling: SamplingText = {
        pixelSize:
            pixelRow === pixelColumn
                ? `${pixelRow} cm`
                : `${pixelRow} × ${pixelColumn} cm`,
        sliceSpacing: voxelCm(slice.sampling.sliceSpacingMm),
        sliceThickness:
            slice.sampling.sliceThicknessMm === undefined
                ? undefined
                : voxelCm(slice.sampling.sliceThicknessMm),
    };
    if (slice.kind === 'oblique') {
        return {
            kind: 'oblique',
            angle: formatAngle(slice.angleDeg),
            cause: {
                acquisitionTilt:
                    slice.cause.acquisitionTiltDeg === undefined
                        ? undefined
                        : formatAngle(slice.cause.acquisitionTiltDeg),
                registrationTilts: slice.cause.registrationTilts,
                viewTurned: slice.cause.viewTurned,
            },
            sampling,
        };
    }
    return {
        kind: 'slice',
        index: slice.index === undefined ? NO_VALUE : String(slice.index + 1),
        count: String(slice.count),
        interpolated: slice.interpolated,
        numbering: {
            from: oppositeDirection(slice.risesToward),
            toward: slice.risesToward,
        },
        sampling,
    };
}

/**
 * One value with its unit: Hounsfield for CT, SUV or activity units for
 * PET, raw signal intensity for every other modality. A non-CT value is
 * NEVER labelled HU, and a PET value is never shown bare.
 */
function formatOneValue(value: CursorValue): string {
    switch (value.kind) {
        case 'hounsfield':
            return withUnit(
                String(Math.round(value.value)),
                ImageUnitSuffix.HU
            );
        case 'pet':
            return formatPetDisplayValue(value.value, value.suvQuantification);
        case 'signal-intensity':
            return `${Math.round(value.value)}`;
    }
}

/**
 * The value under the pointer with its unit. A blended tile reads both of
 * its images, each in its own unit and marked P for the primary and S for
 * the fused secondary, e.g. "P -534 HU / S 812"; the secondary's place
 * holds {@link NO_VALUE} where it has no sample, so neither value can be
 * read as the other's. A tile that shows one image gives its value alone.
 */
export function formatCursorValue(readout: ImageCursorReadout): string {
    const shown = formatOneValue(readout.value);
    if (readout.fusedValue === undefined) {
        return shown;
    }
    const fused = readout.fusedValue
        ? formatOneValue(readout.fusedValue)
        : NO_VALUE;
    return `P ${shown} / S ${fused}`;
}

/**
 * The dose under the pointer as its readout line shows it: the sampled
 * value times the dose's display scale, at 1 decimal, with its unit, e.g.
 * "31.4 Gy". Only an absolute dose is labelled Gy; a relative dose reads
 * "rel.". Undefined when the scaled value is not above 0: the point
 * receives no dose to report.
 *
 * @param displayScale The dimensionless display multiplier of the sampled dose.
 */
export function formatCursorDose(
    dose: DoseCursorValue,
    displayScale: number
): string | undefined {
    const scaled = dose.value * displayScale;
    if (!(scaled > 0)) {
        return undefined;
    }
    return formatDisplayIntensity(scaled, doseUnitLabel(dose.unit));
}

/** The density under the pointer as its readout line shows it. */
export interface DensityLine {
    /**
     * What the densities belong to: the name of the structure whose
     * override is in force, or the Image+ sCT label, so a derived pair is
     * never read as an authored override.
     */
    readonly source: string;
    /** Whether {@link source} names a structure, and is truncated to fit. */
    readonly namesStructure: boolean;
    /**
     * Each quantity the density carries, electron then mass, at the
     * precision of the structure density field and with no unit: the
     * densities are relative to water, e.g. "ρe 1.377".
     */
    readonly quantities: readonly string[];
}

export function formatCursorDensity(
    intl: IntlShape,
    density: DensityCursorValue
): DensityLine {
    const quantity = (messageId: string, value: number | undefined) =>
        value === undefined
            ? []
            : [
                  `${intl.formatMessage({ id: messageId })} ${value.toFixed(
                      DENSITY_OVERRIDE_DECIMALS
                  )}`,
              ];
    const quantities = [
        ...quantity('densityReadout.electron', density.relElectron),
        ...quantity('densityReadout.mass', density.relMass),
    ];
    return density.kind === 'override'
        ? { source: density.roiName, namesStructure: true, quantities }
        : {
              source: intl.formatMessage({ id: 'densityReadout.sct' }),
              namesStructure: false,
              quantities,
          };
}

/**
 * The patient axis a frame turn keeps a plane in: the axis the plane's
 * normal lies along. A turn about it spins the plane within itself.
 */
export const FRAME_AXIS_OF_PLANE: Readonly<Record<Plane, FrameAxis>> = {
    [Plane.Transversal]: 'si',
    [Plane.Sagittal]: 'lr',
    [Plane.Coronal]: 'ap',
};

interface PatientCoordsCm {
    readonly x: string;
    readonly y: string;
    readonly z: string;
}

/** Patient-frame X/Y/Z in cm at 2 dp (macro distance). */
export function formatPatientCoordsCm(point: PointMm): PatientCoordsCm {
    const [x, y, z] = point;
    return {
        x: formatMmAsCm(x, 2),
        y: formatMmAsCm(y, 2),
        z: formatMmAsCm(z, 2),
    };
}

/**
 * Where a plane lies, as one labelled line: its signed offset along its
 * normal in cm at 2 dp, named by the patient axis it is read along, e.g.
 * "Z: 2.50 cm". The offset rounds to the nearest 0.1 mm first, so an
 * offset that rounds to zero prints unsigned.
 */
export function formatPlanePositionLine(position: PlanePosition): string {
    // The offset already carries the axis sign, so it is not flipped again.
    const offsetCm = calculateNormalizedOrientationValue(position.offsetMm, 1);
    return `${position.axis}: ${offsetCm} cm`;
}

/** A patient point as one labelled line, e.g. "X: 0.00 Y: 0.00 Z: 0.90 cm". */
export function formatPatientCoordsLine(point: PointMm): string {
    const coords = formatPatientCoordsCm(point);
    return `X: ${coords.x} Y: ${coords.y} Z: ${coords.z} cm`;
}

/**
 * The modality code to show beside an image's name, or undefined when the
 * name already is it. A series with no description falls back to its
 * modality, and showing both would read as "MR MR".
 */
export function modalityBeside(
    modality: string | undefined,
    label: string
): string | undefined {
    return modality && modality !== label ? modality : undefined;
}

/** Round reference lengths (mm) a scale bar may take. */
const SCALE_BAR_LENGTHS_MM = [1, 2, 5, 10, 20, 50, 100, 200, 500];

export interface ScaleBar {
    /** On-screen bar length in CSS pixels. */
    readonly lengthPx: number;
    /** Labelled physical length in cm, e.g. "5 cm". */
    readonly label: string;
}

function scaleBarLabel(lengthMm: number): string {
    const cm = lengthMm / 10;
    return `${lengthMm % 10 === 0 ? String(cm) : cm.toFixed(1)} cm`;
}

/**
 * Pick a round physical length whose on-screen bar sits in a comfortable width
 * band, for the scale bar. Returns undefined when the display scale is not yet
 * known — the scale is never invented; `mmPerCssPixel` must come from the real
 * view (`RenderAdapter.getMmPerCssPixel`), so the bar always reflects the true
 * on-screen size of a physical length.
 */
export function scaleBarFor(
    mmPerCssPixel: number | undefined,
    availableWidthPx: number
): ScaleBar | undefined {
    if (
        mmPerCssPixel === undefined ||
        !Number.isFinite(mmPerCssPixel) ||
        mmPerCssPixel <= 0 ||
        availableWidthPx <= 0
    ) {
        return undefined;
    }
    const minPx = Math.max(40, availableWidthPx * 0.12);
    const maxPx = Math.max(minPx + 1, availableWidthPx * 0.4);
    const midPx = (minPx + maxPx) / 2;
    let bestMm = SCALE_BAR_LENGTHS_MM[0];
    let bestDiff = Infinity;
    for (const lengthMm of SCALE_BAR_LENGTHS_MM) {
        const px = lengthMm / mmPerCssPixel;
        if (px >= minPx && px <= maxPx) {
            return { lengthPx: px, label: scaleBarLabel(lengthMm) };
        }
        const diff = Math.abs(px - midPx);
        if (diff < bestDiff) {
            bestDiff = diff;
            bestMm = lengthMm;
        }
    }
    return {
        lengthPx: bestMm / mmPerCssPixel,
        label: scaleBarLabel(bestMm),
    };
}

/**
 * Whether a series' named windows are offered. A PET window is read and
 * typed in activity or SUV units, and none of its presets is stated in
 * those units, so none is offered for it.
 */
export function offersWindowPresets(modality: string): boolean {
    return modality !== Modality.PT;
}
